import { observer } from 'mobx-react-lite';
import { lighten } from 'polished';
import React, { ReactElement, useEffect } from 'react';
import DefaultTheme from 'react-dates/lib/theme/DefaultTheme';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import aphroditeInterface from 'react-with-styles-interface-aphrodite';
import ThemedStyleSheet from 'react-with-styles/lib/ThemedStyleSheet';

import Tracking from '@vromo/analytics';
import { useStore } from '@vromo/mobx-react';
import Modals from '@vromo/ui/components/Modals';
import { permissionsCheck } from '@vromo/ui/components/Permissions';
import Toasts from '@vromo/ui/components/Toasts';
import theme from '@vromo/ui/components/theme';
import Notifications from '../Notifications';
import StoreData from '../StoreData';
import { SpatulaActionBarLayout } from '../dispatch/components/SpatulaActionBarLayout';
import Error404 from '../errors/404';
import Unsuccessful from '../errors/Unsuccessful';
import { UpgradePage } from '../freemium/UpgradeModal';
import LoginForwarder from '../login/LoginForwarder';
import SignUp from '../signup/SignUp';
import { RootStore } from '../stores/RootStore';
import { integrationsPageStore } from '../stores/local';
import { PageContext } from '../stores/local/PageStore';
import useAuth from '../useAuth';
import LoggedIn from './LoggedIn';
import OAuthPassthrough from './OAuthPassthrough';
import { AppRoute } from './paths';

const LoginRoot = React.lazy(() => import(/* webpackPrefetch: true */ '../login/Login.lazy'));

const Dashboard = React.lazy(
    () => import(/* webpackPrefetch: true */ '../dashboard/DashboardRoot.lazy')
);
const DispatchPage = React.lazy(
    () => import(/* webpackPrefetch: true */ '../dispatch/Dispatch.lazy')
);
const Plan = React.lazy(() => import(/* webpackPrefetch: true */ '../plan/Plan.lazy'));
const TrackingLink = React.lazy(
    () => import(/* webpackPrefetch: true */ '@vromo/tracking-link/TrackingLink.lazy')
);
const JobRequest = React.lazy(
    () => import(/* webpackPrefetch: true */ '@vromo/vendors/job-request/JobRequest.lazy')
);
const LiveSite = React.lazy(
    () => import(/* webpackPrefetch: true */ '@vromo/vendors/live-site/LiveSite.lazy')
);
const Report = React.lazy(() => import(/* webpackPrefetch: true */ '../report/ReportRoot.lazy'));
const Policies = React.lazy(
    () => import(/* webpackPrefetch: true */ '../policies/PoliciesRoot.lazy')
);
const UserSettings = React.lazy(
    () => import(/* webpackPrefetch: true */ '../account/account-details/AccountSettings')
);
const Campaigns = React.lazy(
    () => import(/* webpackPrefetch: true */ '../campaigns/Campaigns.lazy')
);
const TeamsManager = React.lazy(
    () => import(/* webpackPrefetch: true */ '../groups/TeamsManager.lazy')
);
const Blueprints = React.lazy(
    () => import(/* webpackPrefetch: true */ '../blueprints/Blueprints.lazy')
);
const FindFleets = React.lazy(() => import(/* webpackPrefetch: true */ '../findFleets'));
const IntegrationsPage = React.lazy(
    () => import(/* webpackPrefetch: true */ '../integrations/IntegrationsPage.lazy')
);
const Sites = React.lazy(() => import(/* webpackPrefetch: true */ '../sites/Sites.lazy'));
const ResetPasswordRoot = React.lazy(
    () => import(/* webpackPrefetch: true */ '../login/pages/ResetPassword')
);

ThemedStyleSheet.registerInterface(aphroditeInterface);
ThemedStyleSheet.registerTheme({
    reactDates: {
        ...DefaultTheme.reactDates,
        color: {
            ...DefaultTheme.reactDates.color,
            selected: {
                color: '#fff',
                backgroundColor: theme.tertiary
            },
            selectedSpan: {
                backgroundColor: lighten(0.3, theme.tertiary)
            },
            hoveredSpan: {
                backgroundColor: lighten(0.3, theme.tertiary)
            },
            highlighted: {
                backgroundColor: lighten(0.3, theme.secondary)
            }
        }
    }
});

/** setup the analtycis helpers for the given user account context */
const WithAnalytics = observer<{ children: ReactElement }>(({ children }) => {
    const {
        authStore: { role, user }
    } = useStore<RootStore>('rootStore');
    const { name: groupName, id: groupId } = role.group || {};
    const roleId = role.id;
    const userId = user.id;
    const name = user.contact?.name;
    const email = user.contact?.email;

    useEffect(() => {
        Tracking.identify(userId!.toString(), name, email, { groupId, groupName });
        Tracking.track(
            `Authenticated, tracking started on role '${roleId}' ( ${groupName} ), for user ${email} `
        );
    }, [roleId]);

    return children;
});

function Logout() {
    const [_, logout] = useAuth();

    useEffect(() => {
        logout();
    }, []);

    return <Redirect to="/login" />;
}

export default observer(function VromoRouter() {
    const { authStore } = useStore('rootStore');
    const currentUserPosition = authStore.role.position?.id;
    const isFreemium = authStore.isFreemium;

    useEffect(() => {
        document.getElementById('page-loading').style.display = 'none';
    }, []);
    // usePrintStores()

    return (
        <Router>
            <Modals>
                <Toasts>
                    <Switch>
                        <Route path="/login" component={LoginRoot} exact />

                        <Route path="/logout" component={Logout} exact />

                        <Route path="/signup-iframe" component={SignUp} exact />

                        <Route path="/auto-login" component={LoginForwarder} exact />

                        <Route path="/unsuccessful" component={Unsuccessful} exact />

                        <Route path="/success" component={DispatchPage} exact />

                        <Route path="/track/:auth/:job" component={TrackingLink} exact />

                        <Route path="/job-request/:auth" component={JobRequest} exact />

                        <Redirect from="/delivery-request/:auth" to="/job-request/:auth" />

                        <Route path="/live-site/:auth" component={LiveSite} exact />

                        <Route path="/reset-password" component={ResetPasswordRoot} exact />

                        <Route path="/">
                            <LoggedIn
                                startLoading={() =>
                                    (document.getElementById('page-loading').style.display = 'flex')
                                }
                                stopLoading={() =>
                                    (document.getElementById('page-loading').style.display = 'none')
                                }>
                                <WithAnalytics>
                                    <SpatulaActionBarLayout>
                                        <Switch>
                                            <Route
                                                path="/oauth"
                                                component={OAuthPassthrough}
                                                exact
                                            />
                                            <Route
                                                path="/settings"
                                                component={UserSettings}
                                                exact
                                            />
                                            {authStore.role.id ? (
                                                <StoreData>
                                                    <Notifications />

                                                    <Switch>
                                                        <Route
                                                            path="/"
                                                            component={Dashboard}
                                                            exact
                                                        />
                                                        {permissionsCheck(
                                                            'user',
                                                            currentUserPosition
                                                        ) && [
                                                            <Route
                                                                key="dispatch"
                                                                path="/dispatch/(edit/.+|new)?"
                                                                component={DispatchPage}
                                                                exact
                                                            />,
                                                            <Route
                                                                key="report"
                                                                path="/report"
                                                                component={Report}
                                                                exact
                                                            />
                                                        ]}
                                                        {permissionsCheck(
                                                            'freemiumAdmin',
                                                            currentUserPosition
                                                        ) && (
                                                            <Switch>
                                                                <Route
                                                                    path="/plan"
                                                                    component={Plan}
                                                                    exact
                                                                />

                                                                <Route
                                                                    path="/dispatch/(edit/.+|new)?"
                                                                    component={DispatchPage}
                                                                    exact
                                                                />

                                                                <Route
                                                                    path="/policies"
                                                                    component={Policies}
                                                                />

                                                                <Route
                                                                    path="/job-templates"
                                                                    component={Policies}
                                                                    exact
                                                                />

                                                                <Route
                                                                    path={AppRoute.integrations.toString()}
                                                                    component={() => (
                                                                        <PageContext.Provider
                                                                            value={
                                                                                integrationsPageStore
                                                                            }>
                                                                            <IntegrationsPage />
                                                                        </PageContext.Provider>
                                                                    )}
                                                                />

                                                                <Route
                                                                    path="/sites"
                                                                    component={Sites}
                                                                    exact
                                                                />

                                                                <Route
                                                                    path="/campaigns"
                                                                    component={
                                                                        isFreemium
                                                                            ? UpgradePage
                                                                            : Campaigns
                                                                    }
                                                                    exact
                                                                />

                                                                <Route
                                                                    path="/teams"
                                                                    component={TeamsManager}
                                                                />
                                                                <Route
                                                                    path="/delivery-service-providers"
                                                                    component={FindFleets}
                                                                />
                                                            </Switch>
                                                        )}
                                                        <Route
                                                            path="/dispatch"
                                                            component={DispatchPage}
                                                            exact
                                                        />
                                                        <Route path="*" component={Error404} />
                                                    </Switch>
                                                </StoreData>
                                            ) : (
                                                <Redirect to="/settings" />
                                            )}
                                            <Route path="*" component={Error404} />
                                        </Switch>
                                    </SpatulaActionBarLayout>
                                </WithAnalytics>
                            </LoggedIn>
                        </Route>
                    </Switch>
                </Toasts>
            </Modals>
        </Router>
    );
});
